import CurrentPrice from '../../../../Common/CurrentPrice';
import React, {useEffect, useState} from 'react';
import {Checkbox, Col, Form, Row} from 'antd';
import {showNoPhoneNumberWarning} from '../../Utils/RegistryHelper';
import {FormNavigation} from '../../../../Wizard/Elements';
import {LimitAlarmFormState} from './LimitAlarmFormState';
import {formProps} from '../../../../Wizard/defaults';
import {AlarmLimitSelect} from '../../../../Wizard/FormItems';
import {AlarmLimit} from '../../../../../common/types';
import {DeviceUtils} from '../../../../../common/util';
import {RecipientSelect} from '../../../../Wizard/FormItems/RecipientSelect';
import {RecipientSelectState} from '../../../../Wizard/FormItems/RecipientSelectState';
import {ChannelType} from '../../AddSensor/WizardState';
import SmsDowngradeWarningAlert from '../../Utils/SmsDowngradeWarningAlert';

type LimitAlarmFormProps = {
	channelType: ChannelType;
	currentPrice;
	onBack: () => void;
	onNext: () => void;
	initialState: LimitAlarmFormState;
	moduleSerialNumber: string;
	interval: number;
	onFormStateChange?: (limitAlarmFormState: LimitAlarmFormState) => void;
	unit: number;
	saveStateCallback: (limitAlarmFormState: LimitAlarmFormState) => void;
	sensorIndex: number;
};

const LimitAlarmsForm = ({
	currentPrice,
	onBack,
	onNext,
	channelType,
	initialState,
	moduleSerialNumber,
	interval,
	onFormStateChange,
	unit,
	saveStateCallback,
	sensorIndex,
}: LimitAlarmFormProps) => {
	const [form] = Form.useForm();

	const [useLimitProfileEnabled, setUseLimitProfileEnabled] = useState(!!initialState?.alarmLimit);
	const [alarmLimit, setAlarmLimit] = useState<AlarmLimit>(initialState?.alarmLimit);
	const [recipientSelectState, setRecipientSelectState] = useState<RecipientSelectState>(null);

	const [limitProfilesLoading, setLimitProfilesLoading] = useState(true);
	const [recipientsLoading, setRecipientsLoading] = useState(true);
	const [formStateLoading, setFormStateLoading] = useState(false);
	const loading = limitProfilesLoading || recipientsLoading || formStateLoading;

	const notificationsEnabled: boolean = useLimitProfileEnabled;

	useEffect(() => {
		if (onFormStateChange) {
			onFormStateChange(getFormState());
		}
	}, [useLimitProfileEnabled, alarmLimit, recipientSelectState]);

	const recipientsWithMissingPhoneNumbers = recipientSelectState?.sms.enabled
		? recipientSelectState?.sms.recipients.filter(u => !u.Mobile)
		: [];

	const getFormState = (): LimitAlarmFormState => ({
		alarmLimit: alarmLimit,
		recipients: recipientSelectState,
		unit: unit,
	});

	const onSubmit = _ => {
		setFormStateLoading(true);

		saveStateCallback(getFormState());

		if (recipientsWithMissingPhoneNumbers.length > 0) {
			showNoPhoneNumberWarning(() => {
				onNext();
			}, recipientsWithMissingPhoneNumbers);
		} else {
			onNext();
		}

		setFormStateLoading(false);
	};

	const onNavigateBack = () => {
		setFormStateLoading(true);
		saveStateCallback(getFormState());
		onBack();
		setFormStateLoading(false);
	};

	function onAlarmLimitSelect(alarmLimit: AlarmLimit) {
		setAlarmLimit(alarmLimit);
	}

	function toggleLimitProfileEnabled(enabled: boolean) {
		setUseLimitProfileEnabled(enabled);
		if (!enabled) {
			setAlarmLimit(null);
		}
	}

	return (
		<Form form={form} name="formLimitAlarms" onFinish={onSubmit} layout={'vertical'} requiredMark={false} {...formProps}>
			<Row>
				{sensorIndex !== null && (
					<Col>
						<h4>
							Sensor {sensorIndex + 1}: {channelType.symbolValue}
						</h4>
					</Col>
				)}
				<Col flex={1} />

				<Col>
					<FormNavigation loading={loading} />
				</Col>
			</Row>

			<Form.Item>
				<Form.Item style={{display: 'inline-block'}}>
					<Checkbox
						checked={useLimitProfileEnabled}
						name="use_limit_alarms"
						type="checkbox"
						onChange={event => toggleLimitProfileEnabled(event.target.checked)}
					>
						Use limit profiles
					</Checkbox>
				</Form.Item>
			</Form.Item>

			<AlarmLimitSelect
				disabled={!useLimitProfileEnabled}
				initialAlarmLimit={alarmLimit}
				loggingInterval={interval}
				onSelectCallback={onAlarmLimitSelect}
				outUnit={unit}
				onComponentReady={() => setLimitProfilesLoading(false)}
			/>

			<RecipientSelect
				form={form}
				initialState={initialState?.recipients}
				deviceFamily={DeviceUtils.GetDeviceFamily(moduleSerialNumber)}
				disabled={!notificationsEnabled}
				onValueChanged={setRecipientSelectState}
				onComponentReady={() => setRecipientsLoading(false)}
			/>

			{currentPrice.isSmsDowngradeFromLicenseModel ? <SmsDowngradeWarningAlert /> : null}

			{!DeviceUtils.IsLiberoGx(moduleSerialNumber) && <CurrentPrice currentPrice={currentPrice} />}
			<FormNavigation loading={loading} onBack={onNavigateBack} />
		</Form>
	);
};

export default LimitAlarmsForm;
